import { Color, Spacing } from '@persona-id/design-tokens';
import EditIcon from '@persona-id/design-tokens/icons/EditIcon';
import { startCase } from 'lodash';
import { useMemo, VFC } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import WorkflowObjectFilterConfigPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/WorkflowObjectFilterConfigPanel';
import WorkflowObjectFiltersConfigView from 'dashboard/components/WorkflowControlPanel/StepConfigAction/WorkflowObjectFilterView';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { TriggerObjectType, TriggerObjectTypes } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowActionQueryV1 } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

interface ParamsQueryV1Props {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionQueryV1['params'];
  workflowSchema: WorkflowSchema;
  updateActionParams: (newParams: WorkflowActionQueryV1['params']) => void;
}

const OBJECT_TYPE_OPTIONS = TriggerObjectTypes.map((one) => ({
  value: one,
  label: startCase(one),
}));

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const InputFieldsHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const StyledLabel = styled(Label)`
  padding: ${Spacing[2]} 0;
`;

const ParamsQueryV1: VFC<ParamsQueryV1Props> = ({
  editable,
  errors,
  params,
  workflowSchema,
  updateActionParams,
}) => {
  const configs = useMemo(() => {
    return workflowSchema['filter-field-configs'][params['object-type']] ?? [];
  }, [params, workflowSchema]);

  const [
    showFilterFieldsPanel,
    { toggle: toggleFilterFieldsPanel, disable: closeFilterFieldsPanel },
  ] = useToggle(false);

  const updateParams = <T extends 'object-type' | 'filters'>(
    key: T,
    newValue: WorkflowActionQueryV1['params'][T],
  ) => {
    updateActionParams({
      ...params,
      [key]: newValue,
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionQueryV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel htmlFor='params-query__object-type-select' labelText='Object Type' />
      <Select
        disabled={!editable}
        hasError={hasErrorFor('object-type')}
        id='params-query__object-type-select'
        value={params['object-type']}
        onValueChange={(newObjectType: TriggerObjectType) =>
          updateParams('object-type', newObjectType)
        }
      >
        {OBJECT_TYPE_OPTIONS.map(({ label, value }) => (
          <Option key={value} value={value}>
            {label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('object-type')}
      {params['object-type'] && (
        <>
          <Spacer height={2} />
          <InputFieldsHeading>
            <ParamLabel htmlFor='params-query__filter-fields' labelText='Filter Criteria' />
            <HStack>
              <Button
                id='params-query__filter-fields'
                type='button'
                variant='basic'
                onClick={toggleFilterFieldsPanel}
              >
                <EditIcon />
              </Button>
            </HStack>
          </InputFieldsHeading>
          {renderErrorMessageFor('filters')}
          <Spacer height={2} />
          {params['filters'].length > 0 && (
            <Well>
              <StyledLabel size='sm'>Where</StyledLabel>
              <WorkflowObjectFiltersConfigView
                availableConfigs={configs}
                filters={params['filters']}
              />
            </Well>
          )}
          {showFilterFieldsPanel && (
            <WorkflowObjectFilterConfigPanel
              editable={editable}
              filterConfigs={configs}
              filterCriteria={params['filters']}
              idPrefix='params-query'
              title='Filter Criteria'
              onOutsideClick={closeFilterFieldsPanel}
              updateFilterCriteria={(newFilter) => updateParams('filters', newFilter)}
            />
          )}
        </>
      )}
    </>
  );
};

export default ParamsQueryV1;
